import { useEffect, useState, type ReactNode } from 'react'
import { I18nextProvider } from 'react-i18next'
import { i18n, initI18n } from './config'

interface I18nProviderProps {
  children: ReactNode
  /**
   * Rendered while the initial locale resources load. The first paint is
   * blocked until i18next has the active language bundle so we never flash
   * the raw translation keys (`auth.welcome.title`) to the user.
   */
  fallback?: ReactNode
}

export function I18nProvider({ children, fallback = null }: I18nProviderProps) {
  const [ready, setReady] = useState(i18n.isInitialized)

  useEffect(() => {
    if (i18n.isInitialized) {
      setReady(true)
      return
    }
    let cancelled = false
    initI18n()
      .then(() => {
        if (!cancelled) setReady(true)
      })
      .catch(() => {
        // Boot must not block on missing translations. Render with whatever
        // i18next can do (keys as fallback) rather than leaving the screen
        // blank — the user can still navigate even if labels are raw keys.
        if (!cancelled) setReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (!ready) return <>{fallback}</>
  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}
